import type { Cosmetic, PuzzleDoc, Rc } from '@zinc/shared';
import {
  memo,
  useId,
  useMemo,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { colourOf } from '../../lib/colours';
import type { WrongMarks } from '../../lib/checking';
import { foggedMargin, litCells, startingLight } from '../../lib/fog';
import type { GridState } from '../../lib/game';
import { cellAt, regionEdges, type Geometry } from '../../lib/geometry';
import {
  ConstraintsOver,
  ConstraintsUnder,
  cornerLabels,
  gridFrame,
  ShadingCircles,
  type Frame,
} from './constraints';
import { Cage } from './constraints/Cages';
import { CELL, x0, y0 } from './constraints/geom';

/** The grid is drawn on a 40-unit cell (DEVELOPMENT.md Appendix: style A). */
export { CELL };

const C = {
  thin: '#D3CEC4',
  box: '#2B2A28',
  ink: '#1F1E1C',
  accent: '#2F5FD0',
  mistake: '#C42D3B',
  mistakeBg: '#FBE3E1',
  /** Selection: the whole cell filled, as the v1 yellow was, in a light sky blue (Marko). */
  selection: '#BBDAF8',
  house: '#EEF2FA',
  match: '#DCE6FA',
  muted: '#6B675F',
  /** Fog of war: a cool grey (Pewter), with the thin lines inside it a shade darker. */
  fog: '#C7CED6',
  fogLine: '#B5BBC3',
};

export type MarkStyle = 'sudokupad' | 'simple';

export interface GridProps {
  doc: PuzzleDoc;
  geometry: Geometry;
  state: GridState;
  selection: ReadonlySet<number>;
  /** Simple mode: the selected cell's row, column and box. */
  houseHighlight?: ReadonlySet<number>;
  /** Cells holding the selected digit. */
  matching?: ReadonlySet<number>;
  /** Where a nudge says to look (drawn like matching digits). */
  hint?: ReadonlySet<number>;
  /** Digits to show in red, givens included (and, in simple mode, a red cell). */
  wrong?: ReadonlySet<number>;
  /** Marks to show in red ("Check pencil marks"). */
  wrongMarks?: WrongMarks;
  markStyle: MarkStyle;
  /** Draw the fog as it is before any digit is placed, with or without a solution (thumbnails:
   * their documents carry no solution, and the fog must still hide the puzzle). */
  startingFog?: boolean;
  paused?: boolean;
  /** The part of the drawing to show (zoom and pan); the whole frame by default. */
  view?: Frame;
  onPointerDown?: (cell: number, e: ReactPointerEvent<SVGSVGElement>) => void;
  onPointerMove?: (cell: number, e: ReactPointerEvent<SVGSVGElement>) => void;
  onPointerUp?: (e: ReactPointerEvent<SVGSVGElement>) => void;
  label?: string;
  /** Drawn over the cells' fills, under the constraints and grid lines (the builder's shading). */
  underlay?: ReactNode;
  /** Drawn on top of everything (the builder's candidates and suggestions). */
  overlay?: ReactNode;
}

function pointsAttr(points: [number, number][]): string {
  return points.map(([r, c]) => `${c * CELL},${r * CELL}`).join(' ');
}

function CosmeticShape({ c }: { c: Extract<Cosmetic, { kind: 'shape' }> }) {
  const [r, col] = c.center;
  const w = c.width * CELL;
  const h = c.height * CELL;
  const cx = col * CELL;
  const cy = r * CELL;
  const fill = c.fill ?? 'none';
  const stroke = c.stroke ?? 'none';
  const sw = c.strokeWidth ?? (c.stroke ? 1.5 : 0);
  const transform = c.angle ? `rotate(${c.angle} ${cx} ${cy})` : undefined;
  return (
    <g transform={transform} opacity={c.opacity}>
      {c.rounded ? (
        <ellipse
          cx={cx}
          cy={cy}
          rx={w / 2}
          ry={h / 2}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
      ) : (
        <rect
          x={cx - w / 2}
          y={cy - h / 2}
          width={w}
          height={h}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
      )}
      {c.text !== undefined && c.text !== '' && (
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={c.fontSize ? (c.fontSize * CELL) / 64 : Math.min(h, w) * 0.6}
          fill={c.textColor ?? C.ink}
          fontWeight={600}
        >
          {c.text}
        </text>
      )}
    </g>
  );
}

const Cosmetics = memo(function Cosmetics({
  doc,
  layer,
}: {
  doc: PuzzleDoc;
  layer: 'under' | 'over';
}) {
  return (
    <g pointerEvents="none">
      {doc.cosmetics.map((c, i) => {
        if (c.kind === 'shape') return c.layer === layer ? <CosmeticShape key={i} c={c} /> : null;
        if (layer === 'under' && c.kind === 'line') {
          return (
            <polyline
              key={i}
              points={pointsAttr(c.points)}
              fill={c.fill ?? 'none'}
              stroke={c.color}
              strokeWidth={Math.max(c.width * CELL, 0.5)}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        }
        if (layer === 'over' && c.kind === 'arrow') {
          const pts = c.points;
          const last = pts[pts.length - 1]!;
          const prev = pts[pts.length - 2] ?? last;
          const ang = Math.atan2(last[0] - prev[0], last[1] - prev[1]);
          const hl = (c.headLength ?? 0.3) * CELL;
          const head = [ang + Math.PI * 0.8, ang - Math.PI * 0.8]
            .map(
              (a) =>
                `M${last[1] * CELL + Math.cos(a) * hl},${last[0] * CELL + Math.sin(a) * hl}L${last[1] * CELL},${last[0] * CELL}`,
            )
            .join('');
          return (
            <g
              key={i}
              fill="none"
              stroke={c.color}
              strokeWidth={Math.max(c.width * CELL, 1.5)}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points={pointsAttr(pts)} />
              <path d={head} />
            </g>
          );
        }
        if (layer === 'over' && c.kind === 'cage') {
          return <Cage key={i} cells={c.cells} label={c.label} color={c.color} />;
        }
        return null;
      })}
    </g>
  );
});

const CORNER_SLOTS: [number, number][] = [
  [0.16, 0.2],
  [0.84, 0.2],
  [0.16, 0.82],
  [0.84, 0.82],
  [0.5, 0.2],
  [0.5, 0.82],
  [0.16, 0.51],
  [0.84, 0.51],
  [0.5, 0.51],
];

/** Corner marks with more than nine symbols (16×16): smaller, four along the top and bottom. */
const CORNER_SLOTS_BIG: [number, number][] = [
  [0.14, 0.18],
  [0.86, 0.18],
  [0.14, 0.84],
  [0.86, 0.84],
  [0.38, 0.18],
  [0.62, 0.18],
  [0.38, 0.84],
  [0.62, 0.84],
  [0.14, 0.51],
  [0.86, 0.51],
];

/**
 * Corner mark spots in a cell. Under a label in the top-left corner (a cage total, "×72") the
 * first mark sits just past the label, on the same line (Marko, 2026-11-03). The top-row spots
 * it would crowd move to the bottom row, between the others, so nine marks still fit.
 */
export function cornerSlots(n: number, label = 0): [number, number][] {
  const slots = n > 9 ? CORNER_SLOTS_BIG : CORNER_SLOTS;
  if (label <= 0) return slots;
  const size = n > 9 ? 8.5 : 10;
  // The label's patch (CornerLabel) ends at 3.5 + 5.6 per character; then a gap and half a glyph.
  const x = Math.min(3.5 + label * 5.6 + 1.5 + size * 0.3, CELL * 0.84);
  const top = slots[0]![1];
  const kept = slots.slice(1).filter(([fx, fy]) => fy !== top || fx * CELL >= x + size * 0.9);
  const spare: [number, number][] =
    n > 9
      ? []
      : [
          [0.33, 0.82],
          [0.67, 0.82],
        ];
  return [[x / CELL, top], ...kept, ...spare.slice(0, slots.length - 1 - kept.length)];
}

function pencilSlot(index: number, n: number): [number, number] {
  const side = n <= 4 ? 2 : n <= 9 ? 3 : 4;
  const col = index % side;
  const row = Math.floor(index / side);
  const step = 1 / side;
  return [step * (col + 0.5), step * (row + 0.5)];
}

/** Centre marks: one line, or two when a 16-symbol cell holds many. */
function centreLines(marks: string[], n: number): string[][] {
  if (n <= 9 || marks.length <= 5) return [marks];
  const half = Math.ceil(marks.length / 2);
  return [marks.slice(0, half), marks.slice(half)];
}

const Marks = memo(function Marks({
  geometry,
  state,
  markStyle,
  wrong,
  wrongMarks,
  fogged,
  labels,
}: Pick<GridProps, 'geometry' | 'state' | 'markStyle' | 'wrong' | 'wrongMarks'> & {
  fogged: ReadonlySet<number> | null;
  /** Length of the label in a cell's top-left corner, by cell. */
  labels: ReadonlyMap<number, number>;
}) {
  const n = geometry.digits.length;
  const cornerSize = n > 9 ? 8.5 : 10;
  return (
    <g
      fontFamily="Inter, system-ui, sans-serif"
      style={{ paintOrder: 'stroke' }}
      stroke="#FFFFFF"
      strokeWidth={2}
      strokeLinejoin="round"
      pointerEvents="none"
    >
      {geometry.cells.map(([r, c], i) => {
        const given = fogged?.has(i) ? null : geometry.givens[i];
        const cell = state.cells[i]!;
        const x = x0(c);
        const y = y0(r);
        const big = n > 9 ? 20 : 26;
        if (given != null) {
          return (
            <text
              key={i}
              data-given={i}
              data-wrong={wrong?.has(i) ? 'true' : undefined}
              x={x + CELL / 2}
              y={y + CELL / 2 + 1}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={big}
              fontWeight={600}
              fill={wrong?.has(i) ? C.mistake : C.ink}
            >
              {given}
            </text>
          );
        }
        if (cell.value !== null) {
          return (
            <text
              key={i}
              data-value={i}
              data-wrong={wrong?.has(i) ? 'true' : undefined}
              x={x + CELL / 2}
              y={y + CELL / 2 + 1}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={big}
              fontWeight={400}
              fill={wrong?.has(i) ? C.mistake : C.accent}
            >
              {cell.value}
            </text>
          );
        }
        const out = [];
        const badCorner = wrongMarks?.corner.get(i);
        const badCentre = wrongMarks?.centre.get(i);
        const badPosition = wrongMarks?.position.get(i);
        // Position marks (made in SudokuPad mode, shown in both): each digit in its own spot, as
        // Simple mode's pencil marks are, in the SudokuPad marks' blue.
        for (const m of cell.position) {
          const [fx, fy] = pencilSlot(geometry.digits.indexOf(m), n);
          out.push(
            <text
              key={`pos${m}`}
              data-position={i}
              data-wrong={badPosition?.has(m) ? 'true' : undefined}
              x={x + fx * CELL}
              y={y + fy * CELL}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={n > 9 ? 7.5 : 9}
              fontWeight={500}
              fill={badPosition?.has(m) ? C.mistake : C.accent}
            >
              {m}
            </text>,
          );
        }
        if (markStyle === 'simple') {
          for (const m of cell.corner) {
            const [fx, fy] = pencilSlot(geometry.digits.indexOf(m), n);
            out.push(
              <text
                key={`p${m}`}
                data-pencil={i}
                data-wrong={badCorner?.has(m) ? 'true' : undefined}
                x={x + fx * CELL}
                y={y + fy * CELL}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={n > 9 ? 7.5 : 9}
                fontWeight={600}
                fill={badCorner?.has(m) ? C.mistake : C.muted}
              >
                {m}
              </text>,
            );
          }
        } else {
          const slots = cornerSlots(n, labels.get(i));
          cell.corner.slice(0, slots.length).forEach((m, k) => {
            const [fx, fy] = slots[k]!;
            out.push(
              <text
                key={`c${m}`}
                data-corner={i}
                data-wrong={badCorner?.has(m) ? 'true' : undefined}
                x={x + fx * CELL}
                y={y + fy * CELL}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={cornerSize}
                fontWeight={500}
                fill={badCorner?.has(m) ? C.mistake : C.accent}
              >
                {m}
              </text>,
            );
          });
          if (cell.centre.length > 0) {
            const lines = centreLines(cell.centre, n);
            const longest = Math.max(...lines.map((l) => l.join('').length));
            const size = Math.min(11, (CELL * 0.9) / Math.max(1, longest * 0.6));
            lines.forEach((line, k) => {
              const bad = line.filter((m) => badCentre?.has(m));
              const dy = lines.length === 1 ? 0 : (k - 0.5) * size * 1.05;
              out.push(
                <text
                  key={`centre${k}`}
                  data-centre={i}
                  data-wrong={bad.length ? bad.join('') : undefined}
                  x={x + CELL / 2}
                  y={y + CELL / 2 + 0.5 + dy}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={size}
                  fontWeight={500}
                  fill={C.accent}
                >
                  {bad.length === 0
                    ? line.join('')
                    : line.map((m) => (
                        <tspan key={m} fill={badCentre!.has(m) ? C.mistake : undefined}>
                          {m}
                        </tspan>
                      ))}
                </text>,
              );
            });
          }
        }
        return out.length ? <g key={i}>{out}</g> : null;
      })}
    </g>
  );
});

/**
 * Cell fills (selection, highlights, mistakes, fog) and the player's colours; `beneath` goes
 * between the two (shading circles, which the colours tint).
 */
const CellLayer = memo(function CellLayer({
  geometry: g,
  state,
  selection,
  houseHighlight,
  matching,
  hint,
  wrong,
  markStyle,
  paused,
  fogged,
  beneath,
}: Pick<
  GridProps,
  | 'geometry'
  | 'state'
  | 'selection'
  | 'houseHighlight'
  | 'matching'
  | 'hint'
  | 'wrong'
  | 'markStyle'
  | 'paused'
> & { fogged: ReadonlySet<number> | null; beneath?: ReactNode }) {
  return (
    <>
      {g.cells.map(([r, c], i) => {
        let fill = '#FFFFFF';
        if (!paused) {
          if (fogged?.has(i)) {
            fill = C.fog;
          } else {
            if (houseHighlight?.has(i)) fill = C.house;
            if (matching?.has(i)) fill = C.match;
            if (hint?.has(i)) fill = C.match;
            if (markStyle === 'simple' && wrong?.has(i)) fill = C.mistakeBg;
          }
          if (selection.has(i)) fill = C.selection;
        }
        return (
          <rect
            key={i}
            x={x0(c)}
            y={y0(r)}
            width={CELL}
            height={CELL}
            fill={fill}
            data-cell={i}
            data-fogged={!paused && fogged?.has(i) ? 'true' : undefined}
            data-hint={!paused && hint?.has(i) ? 'true' : undefined}
            data-matching={!paused && !fogged?.has(i) && matching?.has(i) ? 'true' : undefined}
            data-selected={!paused && selection.has(i) ? 'true' : undefined}
          />
        );
      })}
      {!paused && beneath}
      {!paused && (
        <g opacity={0.85} pointerEvents="none">
          {g.cells.map(([r, c], i) => {
            const cols = state.cells[i]!.colours;
            if (cols.length === 0 || selection.has(i)) return null;
            const w = CELL / cols.length;
            return cols.map((k, j) => (
              <rect
                key={`${i}-${k}`}
                x={x0(c) + j * w}
                y={y0(r)}
                width={w}
                height={CELL}
                fill={colourOf(k)}
              />
            ));
          })}
        </g>
      )}
    </>
  );
});

const GridLines = memo(function GridLines({
  geometry: g,
  fogged,
}: {
  geometry: Geometry;
  fogged: ReadonlySet<number> | null;
}) {
  const edges = useMemo(() => regionEdges(g), [g]);
  // Thin lines between two fogged cells are drawn a shade darker than the fog.
  const [thin, thinFog] = useMemo(() => {
    const parts: string[] = [];
    const fogParts: string[] = [];
    g.cells.forEach(([r, c], i) => {
      const right = cellAt(g, r, c + 1);
      const below = cellAt(g, r + 1, c);
      const inFog = (j: number) => fogged !== null && fogged.has(i) && fogged.has(j);
      if (right >= 0) (inFog(right) ? fogParts : parts).push(`M${x0(c + 1)} ${y0(r)}V${y0(r + 1)}`);
      if (below >= 0) (inFog(below) ? fogParts : parts).push(`M${x0(c)} ${y0(r + 1)}H${x0(c + 1)}`);
    });
    return [parts.join(''), fogParts.join('')];
  }, [g, fogged]);
  return (
    <>
      <path d={thin} stroke={C.thin} strokeWidth={1} fill="none" pointerEvents="none" />
      {thinFog && (
        <path d={thinFog} stroke={C.fogLine} strokeWidth={1} fill="none" pointerEvents="none" />
      )}
      <g stroke={C.box} strokeWidth={2.5} strokeLinecap="square" pointerEvents="none">
        {edges.map((e, i) => (
          <line key={i} x1={x0(e.c1)} y1={y0(e.r1)} x2={x0(e.c2)} y2={y0(e.r2)} />
        ))}
      </g>
    </>
  );
});

const PenLines = memo(function PenLines({
  geometry: g,
  lines,
}: {
  geometry: Geometry;
  lines: string[];
}) {
  return (
    <g stroke={C.accent} strokeWidth={3} strokeLinecap="round" opacity={0.8} pointerEvents="none">
      {lines.map((key) => {
        const [a, b] = key.split('-').map(Number) as [number, number];
        const [ra, ca] = g.cells[a] ?? [0, 0];
        const [rb, cb] = g.cells[b] ?? [0, 0];
        return (
          <line
            key={key}
            x1={x0(ca) + CELL / 2}
            y1={y0(ra) + CELL / 2}
            x2={x0(cb) + CELL / 2}
            y2={y0(rb) + CELL / 2}
          />
        );
      })}
    </g>
  );
});

/** Hides the puzzle's own drawing (constraints, cosmetics) in fogged cells and margin squares. */
const FogMask = memo(function FogMask({
  id,
  frame,
  geometry: g,
  fogged,
  margin,
}: {
  id: string;
  frame: Frame;
  geometry: Geometry;
  fogged: ReadonlySet<number>;
  margin: readonly Rc[];
}) {
  // Each fogged square's hole is grown a little so neighbouring holes leave no seam.
  const grow = 0.5;
  const square = ([r, c]: Rc) =>
    `M${x0(c) - grow} ${y0(r) - grow}h${CELL + grow * 2}v${CELL + grow * 2}h${-CELL - grow * 2}Z`;
  const d = [...[...fogged].map((i) => square(g.cells[i]!)), ...margin.map(square)].join('');
  return (
    <defs>
      <mask
        id={id}
        maskUnits="userSpaceOnUse"
        x={frame.x}
        y={frame.y}
        width={frame.w}
        height={frame.h}
      >
        <rect x={frame.x} y={frame.y} width={frame.w} height={frame.h} fill="#FFFFFF" />
        <path d={d} fill="#000000" />
      </mask>
    </defs>
  );
});

/** Where a pointer is on the drawing, in cell units: r = row, c = column (fractional). */
export function pointOf(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
): { r: number; c: number } | null {
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const pt = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
  return { r: pt.y / CELL, c: pt.x / CELL };
}

function pointerCell(
  geometry: Geometry,
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
): number {
  const ctm = svg.getScreenCTM();
  if (!ctm) return -1;
  const pt = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
  return cellAt(geometry, Math.floor(pt.y / CELL), Math.floor(pt.x / CELL));
}

export const Grid = memo(function Grid(props: GridProps) {
  const { doc, geometry: g, state, paused } = props;
  const frame = useMemo(() => gridFrame(doc), [doc]);
  const view = props.view ?? frame;

  // Fogged cells: everything not lit (null when the document has no fog or no solution).
  const fogged = useMemo(() => {
    const lit = props.startingFog
      ? startingLight(g, doc.fog)
      : litCells(
          g,
          doc.fog,
          state.cells.map((c) => c.value),
        );
    if (!lit) return null;
    return new Set(g.cells.flatMap((_, i) => (lit.has(i) ? [] : [i])));
  }, [g, doc.fog, state, props.startingFog]);
  // The margin around a grid smaller than its canvas is fogged too (its outside clues).
  const margin = useMemo(
    () =>
      fogged
        ? (foggedMargin(
            g,
            doc.fog,
            state.cells.map((c) => c.value),
            props.startingFog,
          ) ?? [])
        : [],
    [g, doc.fog, state, props.startingFog, fogged],
  );
  const maskId = `fog-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`;
  const mask = fogged && (fogged.size > 0 || margin.length > 0) ? `url(#${maskId})` : undefined;
  // Corner labels the player can see (the fog hides the rest), for the corner marks to avoid.
  const allLabels = useMemo(() => cornerLabels(doc), [doc]);
  const labels = useMemo(() => {
    const out = new Map<number, number>();
    for (const [rc, text] of allLabels) {
      const [r, c] = rc.split(',').map(Number) as [number, number];
      const i = cellAt(g, r, c);
      if (i >= 0 && !fogged?.has(i)) out.set(i, text.length);
    }
    return out;
  }, [allLabels, g, fogged]);

  const onDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (props.onPointerDown)
      props.onPointerDown(pointerCell(g, e.currentTarget, e.clientX, e.clientY), e);
  };
  const onMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (props.onPointerMove)
      props.onPointerMove(pointerCell(g, e.currentTarget, e.clientX, e.clientY), e);
  };

  return (
    <svg
      viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
      role="img"
      aria-label={props.label ?? 'Sudoku grid'}
      className="block h-auto w-full touch-none select-none"
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={props.onPointerUp}
      onPointerCancel={props.onPointerUp}
    >
      {mask && !paused && (
        <FogMask id={maskId} frame={frame} geometry={g} fogged={fogged!} margin={margin} />
      )}
      {!paused &&
        margin.map(([r, c]) => (
          <rect
            key={`fog-${r}-${c}`}
            x={x0(c)}
            y={y0(r)}
            width={CELL}
            height={CELL}
            fill={C.fog}
            data-fogged-margin={`${r},${c}`}
            pointerEvents="none"
          />
        ))}
      <CellLayer
        geometry={g}
        state={state}
        selection={props.selection}
        houseHighlight={props.houseHighlight}
        matching={props.matching}
        hint={props.hint}
        wrong={props.wrong}
        markStyle={props.markStyle}
        paused={paused}
        fogged={fogged}
        beneath={
          doc.constraints.some((e) => e.type === 'view-count' || e.type === 'area-clues') && (
            <g mask={mask}>
              <ShadingCircles doc={doc} />
            </g>
          )
        }
      />
      {!paused && props.underlay}
      {!paused && (
        <g mask={mask}>
          <Cosmetics doc={doc} layer="under" />
          <ConstraintsUnder doc={doc} geometry={g} />
        </g>
      )}
      <GridLines geometry={g} fogged={paused ? null : fogged} />
      {!paused && (
        <>
          <g mask={mask}>
            <ConstraintsOver doc={doc} />
            <Cosmetics doc={doc} layer="over" />
          </g>
          <PenLines geometry={g} lines={state.lines} />
          <Marks
            geometry={g}
            state={state}
            markStyle={props.markStyle}
            wrong={props.wrong}
            wrongMarks={props.wrongMarks}
            fogged={fogged}
            labels={labels}
          />
          {props.overlay}
        </>
      )}
    </svg>
  );
});
